import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

export interface GlobalSearchTarget {
    id: string
    /** The router navigation key — unique per selection, so re-selecting the same entity yields a NEW target. */
    nav: string
}

/**
 * Captures the global-search highlight target for the CURRENT navigation.
 *
 * Keyed on `location.key` (not the id), so re-selecting the *same* entity is treated as a fresh
 * trigger and re-fires the highlight. Clears history state after capture so a refresh/back-nav
 * doesn't re-trigger, and auto-clears after 5s so a stale id can't leak into later navigations.
 */
export function useGlobalSearchTarget(): GlobalSearchTarget | null {
    const { key, state } = useLocation()
    const [target, setTarget] = useState<GlobalSearchTarget | null>(null)
    const handledNav = useRef<string | null>(null)

    useEffect(() => {
        const incoming = (state as { globalSearchHighlightId?: string } | null)?.globalSearchHighlightId ?? null
        if (!incoming || handledNav.current === key) return
        handledNav.current = key
        setTarget({ id: incoming, nav: key })
        // Clear from history state so a refresh / back-nav doesn't re-trigger the highlight.
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }, [key, state])

    useEffect(() => {
        if (!target) return
        const timer = setTimeout(() => setTarget(null), 5000)
        return () => clearTimeout(timer)
    }, [target])

    return target
}

/**
 * The active global-search highlight id (re-emits per navigation — see {@link useGlobalSearchTarget}).
 */
export function useGlobalSearchHighlight(): string | null {
    return useGlobalSearchTarget()?.id ?? null
}

/**
 * Auto-scroll to and flash-highlight the element matching `[data-search-id="<id>"]`.
 * Call this hook on any page that should react to global-search navigation; rows just need a
 * `data-search-id={item.id}` attribute. Works with async-loaded / virtualized lists via a
 * MutationObserver (gives up after 10s).
 *
 * Re-firing: keyed on the navigation, so selecting the same entity again re-scrolls and replays
 * the flash (the CSS animation is restarted with a forced reflow even when the row is already on screen).
 */
export function useGlobalSearchScrollHighlight(): void {
    const target = useGlobalSearchTarget()

    useEffect(() => {
        if (!target) return
        const { id } = target
        let flashTimer: ReturnType<typeof setTimeout> | undefined
        let observer: MutationObserver | undefined
        let giveUp: ReturnType<typeof setTimeout> | undefined

        const tryHighlight = (): boolean => {
            const el = document.querySelector(`[data-search-id="${CSS.escape(id)}"]`)
            if (!el) return false
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            // Restart the CSS flash even if the class is still on the element (re-selecting the same row):
            // remove → force reflow → re-add so the animation replays.
            el.classList.remove('global-search-highlight')
            void (el as HTMLElement).offsetWidth
            el.classList.add('global-search-highlight')
            flashTimer = setTimeout(() => el.classList.remove('global-search-highlight'), 3000)
            return true
        }

        if (!tryHighlight()) {
            observer = new MutationObserver(() => {
                if (tryHighlight()) observer?.disconnect()
            })
            observer.observe(document.body, { childList: true, subtree: true })
            giveUp = setTimeout(() => observer?.disconnect(), 10000)
        }

        return () => {
            observer?.disconnect()
            if (giveUp) clearTimeout(giveUp)
            if (flashTimer) clearTimeout(flashTimer)
        }
    }, [target])
}
